import { useEffect, useMemo, useState } from 'react';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import DatePicker from 'react-datepicker';
import { AiOutlinePlus } from 'react-icons/ai';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FaArrowRightLong } from 'react-icons/fa6';
import { IoMdArrowDropdown, IoMdArrowDropright } from 'react-icons/io';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  checkScore,
  getCategories,
  getItems,
  openModal,
  registerItems,
} from '../actions/warranty';
import Button from '../components/ui/Button/Button';
import { Input } from '../components/ui/Input/Input';
import { Select } from '../components/ui/Select/Select';
import { CheckBox, InfoTooltip, RequiredField } from '../utils/components';
import {
  calcDate,
  formatMySQLdate,
  marketplaces,
  validateRegister,
} from '../utils/constants';

const steps = [
  { id: 1, label: 'Personal Details' },
  { id: 2, label: 'Product Details' },
  { id: 3, label: 'Order Details' },
];

const Register = ({
  warranty: {
    categories,
    itemsByCategory,
    score,
    loadingWarranty,
    getScore,
    loadingCategories,
    loadingItemsByCategory,
  },
  getItems,
  getCategories,
  registerItems,
  openModal,
  checkScore,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!getScore) checkScore('submit');
  }, [checkScore, getScore]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    country: '',
    state: '',
    city: '',
    zip: '',
    address: '',
    order_number: '',
    marketplace: '',
    purchase_date: '',
    set_password: '',
    password2: '',
    needUser: false,
    redirect: null,
    email_consent: false,
  });

  const [products, setProducts] = useState([{ category: '', id: '' }]);
  const [required, setRequired] = useState({
    first_name: { filled: false, message: '' },
    last_name: { filled: false, message: '' },
    email: { filled: false, message: '' },
    order_number: { filled: false, message: '' },
    marketplace: { filled: false, message: '' },
    purchase_date: { filled: false, message: '' },
    set_password: { filled: false, message: '' },
    password2: { filled: false, message: '' },
  });

  const [requiredProducts, setRequiredProducts] = useState([
    {
      category: { filled: false, message: '' },
      id: { filled: false, message: '' },
    },
  ]);

  const [step, setStep] = useState(1);
  const [isOpen, setOpen] = useState(false);

  const {
    country,
    state,
    first_name,
    last_name,
    email,
    city,
    zip,
    address,
    order_number,
    marketplace,
    purchase_date,
    set_password,
    password2,
    needUser,
    redirect,
  } = formData;
  const selectCountry = (value) => setFormData({ ...formData, country: value });
  const selectRegion = (value) => setFormData({ ...formData, state: value });

  const changeStep = (e) => {
    const index = +e.currentTarget.getAttribute('data-index');
    if (index) {
      if (index < step) {
        setStep(index);
        return;
      }

      let data = {};
      if (step !== 2) {
        data = validateRegister({ old: required, values: formData, step });
        setRequired(data.newValues);
      } else {
        data = validateRegister({
          old: requiredProducts,
          values: products,
          step,
        });
        setRequiredProducts(data.newValues);
      }
      if (!data.preventChange) {
        if (index === 2 && !categories.length) getCategories();
        setStep(index);
      }
    }
  };

  const changeItem = (e) => {
    const currentIndex = +e.target.getAttribute('data-index');

    const { name, value } = e.target;
    setProducts(
      products.map((product, index) =>
        index === currentIndex
          ? {
              ...product,
              [name]: value,
              ...(name === 'category' ? { id: '' } : {}),
            }
          : product,
      ),
    );

    if (
      name === 'category' &&
      value &&
      !itemsByCategory[value] &&
      !loadingItemsByCategory[value]
    ) {
      getItems(value);
    }
  };

  const changeFormData = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const openAddress = (e) => {
    setOpen(!isOpen);
    setFormData({ ...formData, country: 'United States' });
  };

  const deleteProduct = (e) => {
    const index = +e.currentTarget.getAttribute('data-index');
    if (index) {
      setProducts(products.filter((p, i) => i !== index));
      setRequiredProducts(requiredProducts.filter((p, i) => i !== index));
    }
  };

  const addProduct = (e) => {
    setProducts([...products, { category: '', id: '' }]);
    setRequiredProducts([
      ...requiredProducts,
      {
        category: { filled: false, message: '' },
        id: { filled: false, message: '' },
      },
    ]);
  };

  const createUser = (e) =>
    setFormData({ ...formData, needUser: !formData.needUser });

  const changeEmailPolicy = (e) =>
    setFormData({ ...formData, email_consent: !formData.email_consent });

  const setPurchaseDate = (date) =>
    setFormData({ ...formData, purchase_date: date });

  const registerItem = async (e) => {
    const data = validateRegister({ old: required, values: formData, step });
    setRequired(data.newValues);

    if (data.preventChange) return;

    const requestData = {
      products,
      date: formatMySQLdate(new Date()),
    };
    const notData = ['needUser', 'password2', 'redirect'];
    for (const key in formData) {
      const isValidValue =
        formData[key] ||
        typeof formData[key] === 'boolean' ||
        formData[key] === 0;

      if (isValidValue && !notData.includes(key)) {
        let value = formData[key];

        if (key === 'purchase_date') {
          value = formatMySQLdate(
            new Date(formData[key].setHours(10, 0, 0, 0)),
          );
        } else if (key === 'email_consent') {
          value = value ? 1 : 0;
        }

        requestData[key] = value;
      }
    }

    const res = await registerItems(requestData);
    if (res) {
      setFormData({ ...formData, redirect: <Redirect to='/' /> });
    }
  };

  const categoriesOptions = useMemo(() => {
    return categories.map((category) => {
      return { value: category, label: category };
    });
  }, [categories]);

  const marketplacesOptions = useMemo(() => {
    return marketplaces.map((marketplace) => {
      return { value: marketplace, label: marketplace };
    });
  }, []);

  if (redirect) return redirect;

  return (
    <div className='register-container'>
      <div className='register-steps-header'>
        <a href='/' className='breadcrumbs-link'>
          Warranty
        </a>
        <FaArrowRightLong className='register-header-icon' />
        <span>WARRANTY REGISTRATION</span>
      </div>
      <div className='register-steps-box'>
        <div className='register-list'>
          <ul className='register-list-box'>
            {steps.map((s) => (
              <li
                key={s.id}
                className={`register-list-row ${
                  step === s.id ? 'register-list-row-active' : ''
                }`}
                data-index={s.id}
                onClick={changeStep}
              >
                {s.label}
              </li>
            ))}
          </ul>
        </div>
        <div className='register-form'>
          <div
            className={`register-form-box ${
              step === 1 ? 'register-active-form' : ''
            }`}
          >
            <div className='register-form-box-body'>
              <h3 className='register-list-row-active'>{steps[0].label}</h3>
              <ul className='register-form-body'>
                <li className='register-form-row'>
                  <Input
                    type='text'
                    label='First Name'
                    value={first_name}
                    onChange={changeFormData}
                    name='first_name'
                    error={required.first_name.message}
                    className='register-form__input'
                    required
                  />
                </li>
                <li className='register-form-row'>
                  <Input
                    type='text'
                    label='Last Name'
                    value={last_name}
                    onChange={changeFormData}
                    name='last_name'
                    error={required.last_name.message}
                    className='register-form__input'
                    required
                  />
                </li>
                <li className='register-form-row'>
                  <Input
                    type='email'
                    label='Email'
                    value={email}
                    onChange={changeFormData}
                    name='email'
                    error={required.email.message}
                    className='register-form__input'
                    required
                  />
                </li>
                <li className='register-form-row'>
                  <div className='register-form-open' onClick={openAddress}>
                    Add an address
                    {isOpen ? (
                      <IoMdArrowDropdown className='add-address-icon' />
                    ) : (
                      <IoMdArrowDropright className='add-address-icon' />
                    )}
                  </div>
                </li>
              </ul>
              <ul
                style={{ display: isOpen ? '' : 'none' }}
                className='register-form-sublist'
              >
                <li className='register-form-row register-form-address-row register-form-sublist--end'>
                  <Input
                    type='text'
                    label='Address'
                    value={address}
                    onChange={changeFormData}
                    name='address'
                    className='register-form__input'
                  />
                </li>
                <li className='register-form-row register-form-address-row register-form-sublist--end'>
                  <Input
                    type='text'
                    label='City'
                    value={city}
                    onChange={changeFormData}
                    name='city'
                    className='register-form__input'
                  />
                </li>
                <li className='register-form-row register-form-address-row register-form-sublist--end'>
                  <label className='register-form-select__label'>
                    Province/State
                  </label>
                  <div className='register-form-select__wrapper'>
                    <RegionDropdown
                      blankOptionLabel='No Country selected'
                      defaultOptionLabel='Select a region'
                      country={country}
                      value={state}
                      onChange={selectRegion}
                      classes='register-input'
                    />
                    <IoMdArrowDropdown className='register-form-select-icon' />
                  </div>
                </li>
                <li className='register-form-row register-form-address-row register-form-sublist--end'>
                  <Input
                    type='text'
                    label='ZIP'
                    value={zip}
                    onChange={changeFormData}
                    name='zip'
                    className='register-form__input'
                  />
                </li>
                <li className='register-form-row register-form-address-row register-form-sublist--end'>
                  <label className='register-form-select__label'>Country</label>
                  <div className='register-form-select__wrapper'>
                    <CountryDropdown
                      defaultOptionLabel='Select a Country'
                      value={country}
                      onChange={selectCountry}
                      classes='register-input'
                    />
                    <IoMdArrowDropdown className='register-form-select-icon' />
                  </div>
                </li>
              </ul>
            </div>
            <div className='register-form-btns'>
              <Button
                data-index='2'
                onClick={changeStep}
                disabled={score < 0.5}
                className='register-form-button'
              >
                NEXT
              </Button>
            </div>
          </div>
          <div
            className={`register-form-box ${
              step === 2 ? 'register-active-form' : ''
            }`}
          >
            <div className='register-form-box-body'>
              <h3 className='register-list-row-active'>{steps[1].label}</h3>
              <ul className='register-form-product-list'>
                {products.map((item, i) => (
                  <li className='register-form-product' key={`item-${i}`}>
                    {loadingCategories ? (
                      <div className='form-select register-form-product__select'>
                        <label className='form-select__label'>
                          Product Category
                          <RequiredField />
                        </label>
                        <div className='register-input-loader skeleton-box'></div>
                        <p className='form-select__error'></p>
                      </div>
                    ) : (
                      <Select
                        label='Product Category'
                        name='category'
                        options={categoriesOptions}
                        value={item.category}
                        onChange={changeItem}
                        placeholder='Select Category'
                        data-index={i}
                        error={requiredProducts[i].category.message}
                        containerClassName='register-form-product__select'
                        required
                      />
                    )}

                    {loadingItemsByCategory[item.category] ? (
                      <div className='form-select register-form-product__select'>
                        <label className='form-select__label'>
                          Product Name
                          <RequiredField />
                        </label>
                        <div className='register-input-loader skeleton-box'></div>
                        <p className='form-select__error'></p>
                      </div>
                    ) : (
                      <Select
                        label='Product Name'
                        name='id'
                        options={(itemsByCategory[item.category] || []).map(
                          (product) => ({
                            value: product.id,
                            label: product.name,
                          }),
                        )}
                        value={item.id}
                        onChange={changeItem}
                        placeholder='Select Product'
                        data-index={i}
                        error={requiredProducts[i].id.message}
                        containerClassName='register-form-product__select'
                        required
                        disabled={!item.category}
                      />
                    )}
                    <div className='register-form-remove'>
                      {i ? (
                        <FaRegTrashAlt
                          className='register-form-remove-icon'
                          onClick={deleteProduct}
                          data-index={i}
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
              <div className='register-form-add' onClick={addProduct}>
                <AiOutlinePlus className='register-form-add-icon' />
                Add product
              </div>
            </div>
            <div className='register-form-btns'>
              <Button
                data-index='1'
                onClick={changeStep}
                variant='secondary'
                className='register-form-button'
              >
                BACK
              </Button>
              <Button
                data-index='3'
                onClick={changeStep}
                disabled={score < 0.5}
                className='register-form-button'
              >
                NEXT
              </Button>
            </div>
          </div>
          <div
            className={`register-form-box ${
              step === 3 ? 'register-active-form' : ''
            }`}
          >
            <div className='register-form-box-body'>
              <h3 className='register-list-row-active'>{steps[2].label}</h3>
              <ul className='register-form-body'>
                <li className='register-form-row'>
                  <Select
                    label='Marketplace'
                    name='marketplace'
                    options={marketplacesOptions}
                    value={marketplace}
                    onChange={changeFormData}
                    placeholder='Select Marketplace'
                    error={required.marketplace.message}
                    containerClassName='register-form-product__select'
                    required
                  />
                </li>
                <li className='register-form-row'>
                  <label className='register-form-select__label'>
                    Purchase Date
                    <RequiredField />
                  </label>
                  <div className='date-picker-wrapper'>
                    <DatePicker
                      selected={purchase_date}
                      onChange={setPurchaseDate}
                      minDate={calcDate(60)}
                      maxDate={new Date()}
                      className={`register-input ${
                        required.purchase_date.filled
                          ? 'register-input-error'
                          : ''
                      }`}
                      placeholderText='Select a date'
                      calendarClassName='custom-calendar'
                    />
                  </div>
                  <p className='register-error-message'>
                    {required.purchase_date.message}
                  </p>
                </li>
                <li className='register-form-row'>
                  <span
                    className='register-form-modal'
                    onClick={() => {
                      openModal({ type: 'info' });
                    }}
                  >
                    How do I find it?
                  </span>
                  <Input
                    label='Order Number'
                    name='order_number'
                    type='text'
                    value={order_number}
                    onChange={changeFormData}
                    error={required.order_number.message}
                    className='register-form__input'
                    required
                  />
                </li>
              </ul>
              <form>
                <li className='register-form-row'>
                  <div className='register-form-open'>
                    <CheckBox checked={needUser} onChange={createUser} />
                    Register customer account on gritrgear.com
                  </div>
                  <p className='register-form-row-text'>
                    By creating an account, you agree to our{' '}
                    <a
                      href='https://gritrgear.com/offer-terms-conditions/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='register-small-link'
                    >
                      Terms
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://gritrgear.com/privacy-and-security-policy/'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='register-small-link'
                    >
                      Privacy Policy
                    </a>
                  </p>
                </li>
                <ul
                  style={{ display: needUser ? '' : 'none' }}
                  className='register-form-sublist register-form-sublist--start'
                >
                  <li className='register-form-row register-form-address-row'>
                    <InfoTooltip
                      className='register-tooltip__icon'
                      align='bottom'
                      text='Specify the password for further log in to gritrgear.com. Passwords must be at least 7 characters and contain both alphabetic and numeric characters.'
                    />
                    <Input
                      label='Password'
                      name='set_password'
                      type='password'
                      value={set_password}
                      onChange={changeFormData}
                      error={required.set_password.message}
                      className='register-form__input'
                      required
                    />
                  </li>
                  <li className='register-form-row register-form-address-row'>
                    <InfoTooltip
                      className='register-tooltip__icon register-tooltip__icon--right'
                      align='bottom'
                      text='Specify the password for further log in to gritrgear.com. Passwords must be at least 7 characters and contain both alphabetic and numeric characters.'
                    />
                    <Input
                      label='Confirm Password'
                      name='password2'
                      type='password'
                      value={password2}
                      onChange={changeFormData}
                      error={required.password2.message}
                      className='register-form__input'
                      required
                    />
                  </li>
                </ul>
              </form>
              <li className='register-form-row'>
                <div className='register-form-open'>
                  <CheckBox
                    checked={formData.email_consent}
                    onChange={changeEmailPolicy}
                  />
                  I agree to receive marketing emails
                </div>
              </li>
            </div>
            <div className='register-form-btns'>
              <Button
                data-index='2'
                onClick={changeStep}
                variant='secondary'
                className='register-form-button'
              >
                BACK
              </Button>
              <Button
                onClick={registerItem}
                disabled={loadingWarranty || score < 0.5}
                className='register-form-button'
              >
                SUBMIT
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  warranty: state.warranty,
});

export default connect(mapStateToProps, {
  getItems,
  getCategories,
  registerItems,
  openModal,
  checkScore,
})(Register);
